import React, { useMemo, useState } from "react";

import { LoadingSlideNoText } from "@components/title-slide-view";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { usePageConfiguration } from "@features/sales-v3/hooks";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type GetCampaign_me_adObject,
  type GetSubLevelBreakdown,
  type GetSubLevelBreakdownVariables,
} from "@north-beam/nb-client-side";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { reportStateToGQLVariables } from "@pages/objects/report-state";
import { GET_SUB_LEVEL_BREAKDOWN } from "@pages/objects/sub-level-breakdown";
import { RadioGroup } from "@shared/radio-group/radio-group";

import { Table } from "./table";
import { type SubLevel } from "./types";

function getGranularityChoices(level: SubLevel) {
  if (level === "campaign") {
    return [
      { label: "Adset", value: "adset" },
      { label: "Ad", value: "ad" },
    ];
  }

  return [{ label: "Ad", value: "ad" }];
}

interface Props {
  adObject: GetCampaign_me_adObject;
  adObjectLevel: SubLevel;
  onClickAdObject(id: string): void;
}

export const TableSection = ({
  adObject,
  adObjectLevel,
  onClickAdObject,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const config = usePageConfiguration();

  const anchor = todayLocalAsISODate();
  const gqlVariables = useMemo(() => {
    return reportStateToGQLVariables(
      {
        dateRange: config.dateRange,
        // TODO: Fix this
        compareDateRange: config.compareDateRange as any,
        attributionModel: config.attributionModel,
        attributionWindow: config.attributionWindow,
        nbAccountingMode: config.accountingMode,
        timeGranularity: config.timeGranularity,
      },
      anchor,
    );
  }, [config, anchor]);

  const granularityChoices = useMemo(() => {
    return getGranularityChoices(adObjectLevel);
  }, [adObjectLevel]);

  const [granularity, setGranularity] = useState(granularityChoices[0].value);

  const granularityOption = useMemo(() => {
    return granularityChoices.find((e) => e.value === granularity)!;
  }, [granularity, granularityChoices]);

  const { data, loading } = useNorthbeamQuery<
    GetSubLevelBreakdown,
    GetSubLevelBreakdownVariables
  >(GET_SUB_LEVEL_BREAKDOWN, {
    useHeavy: true,
    variables: {
      id: adObject.id,
      granularity,
      ...gqlVariables,
    },
  });

  if (loading) {
    return (
      <div className="mt-20">
        <LoadingSlideNoText />
      </div>
    );
  }

  const metrics = data?.me.adObject?.subLevelBreakdown;

  if (!metrics) {
    return <div className="text-center p-12">No data for this time range</div>;
  }

  return (
    <>
      {granularityChoices.length > 1 && (
        <div className="mt-8">
          <h3 className="text-xs font-bold">GRANULARITY</h3>
          <RadioGroup
            className="flex gap-7"
            options={granularityChoices}
            onChange={(value) => {
              prettyLogEventPage({
                domain: "Flyout",
                action: "onSelect",
                additionalActionInfo: "Flyout performance change granularity",
                data: {
                  newGranularityChoices: value,
                  pageContext: { granularityChoices },
                },
              });

              setGranularity(value);
            }}
            value={granularity}
          />
        </div>
      )}
      <div className="mt-8">
        <h3 className="text-lg font-bold">
          ({metrics.rows.length}) {granularityOption.label}s
        </h3>
        <div className="h-[20rem]">
          <Table
            adObjectLevel={adObjectLevel}
            rows={metrics.rows}
            onClickAdObject={onClickAdObject}
          />
        </div>
      </div>
    </>
  );
};
